import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select/async';
import { FormFeedback } from 'reactstrap';
import { get } from 'lodash';

import { validationCheckErrorType } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import { checkHadError } from 'utils';

const AppAsyncSelectValidation = (props) => {
  const {
    isHiddenClearable,
    isHiddenSearchable,
    isMulti,
    isGroup,
    isShowError,
    isDisabled,
    isLoading,

    checkErrorType,
    keyName,
    name,
    id,
    placeholder,
    selectedClassName,
    subTitle,
    value,
    isUsePureValue,
    fontSize,

    validation,
    options,
    defaultOptions,

    onBlur,
    onChange,
    onChangeMulti,
    onSearch,
    onMenuClose,
  } = props;

  const keyCheckError = useMemo(() => (keyName || name), [keyName, name]);

  const isHadError = useMemo(() => checkHadError(
    validation, keyCheckError, checkErrorType,
  ), [validation, keyCheckError, checkErrorType]);

  const selectInvalid = useMemo(() => ({
    control: (provided) => ({
      ...provided,
      border: '1px solid var(--bookese-danger-color) !important',
      boxShadow: 'var(--bookese-danger-color) !important',
    }),
  }), []);

  const stylesCustom = useMemo(() => ({
    control: (provided, state) => ({
      ...provided,
      boxShadow: state.isFocused ? 'var(--bookese-blue-100)' : '',
      borderColor: state.isFocused ? 'var(--bookese-blue-100) !important' : 'var(--bookese-gray-200)',
      fontSize: `${fontSize} !important`,
      minHeight: '36px',
      height: 'fit-content',
      cursor: 'pointer',
    }),

    singleValue: (provided) => ({
      ...provided,
      fontSize: `${fontSize} !important`,
    }),

    placeholder: (provided) => ({
      ...provided,
      fontSize: `${fontSize} !important`,
    }),

    input: (provided) => ({
      ...provided,
      fontSize: `${fontSize} !important`,
    }),

    option: (provided) => ({
      ...provided,
      cursor: 'pointer',
    }),
  }), [fontSize]);

  const finalValue = React.useMemo(() => {
    switch (true) {
      case isUsePureValue:
      case isMulti: {
        return value;
      }

      case isGroup: {
        const selectedOption = options
          .flatMap((group) => group.options)
          .find((item) => item.value === value);

        return selectedOption || null;
      }

      default: {
        return options.filter?.((item) => item.value === value);
      }
    }
  }, [isMulti, isGroup, isUsePureValue, options, value]);

  const onChangeCallback = React.useCallback((e) => {
    onChange({ ...e, name, id });
  }, [id, name, onChange]);

  return (
    <>
      <Select
        options={options}
        defaultOptions={defaultOptions}
        value={finalValue}
        name={name}
        id={id}
        inputId={name}
        isMulti={isMulti}
        classNamePrefix="select2-selection"
        className={`basic-multi-select ${selectedClassName}`}
        isLoading={isLoading}
        isSearchable={isHiddenSearchable}
        isClearable={isHiddenClearable}
        placeholder={placeholder}
        onBlur={validation.handleBlur || onBlur}
        onChange={isMulti ? onChangeMulti : onChangeCallback}
        noOptionsMessage={({ option }) => (!option && 'Không có lựa chọn')}
        styles={isShowError && isHadError ? selectInvalid : stylesCustom}
        invalid={isShowError && isHadError}
        isDisabled={isDisabled}
        loadOptions={onSearch}
        onMenuClose={onMenuClose}
      />

      {
        subTitle && <div className="Selection-SubTitle">{subTitle}</div>
      }

      {
        isShowError && isHadError && (
          <FormFeedback type="invalid" className="d-block font-size-12">
            {get(validation.errors, keyCheckError, '')}
          </FormFeedback>
        )
      }
    </>
  );
};

AppAsyncSelectValidation.propTypes = {
  isHiddenClearable: PropTypes.bool,
  isHiddenSearchable: PropTypes.bool,
  isMulti: PropTypes.bool,
  isGroup: PropTypes.bool,
  isShowError: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,

  checkErrorType: PropTypes.oneOf([
    '',
    validationCheckErrorType.ERR_ONLY,
    validationCheckErrorType.TOUCHED_ONLY,
  ]),
  selectedClassName: PropTypes.string,
  keyName: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  subTitle: PropTypes.string,
  fontSize: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Object),
  ]),
  isUsePureValue: PropTypes.bool,

  placeholder: PropTypes.string,

  validation: PropTypes.instanceOf(Object),
  options: PropTypes.instanceOf(Array).isRequired,
  defaultOptions: PropTypes.instanceOf(Array),

  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onChangeMulti: PropTypes.func,
  onSearch: PropTypes.func,
  onMenuClose: PropTypes.func,
};

AppAsyncSelectValidation.defaultProps = {
  isHiddenClearable: false,
  isHiddenSearchable: false,
  isMulti: false,
  isGroup: false,
  isShowError: true,
  isDisabled: false,
  isLoading: false,

  checkErrorType: '',
  keyName: '',
  name: '',
  id: '',
  fontSize: '13px',
  placeholder: 'Chọn',
  selectedClassName: '',
  subTitle: '',
  value: '',
  isUsePureValue: false,

  validation: INITIAL_VALUE.OBJECT,
  defaultOptions: INITIAL_VALUE.ARRAY,

  onBlur: INITIAL_VALUE.FUNCTION,
  onChangeMulti: INITIAL_VALUE.FUNCTION,
  onSearch: INITIAL_VALUE.FUNCTION,
  onMenuClose: INITIAL_VALUE.FUNCTION,
};

export default memo(AppAsyncSelectValidation);
